import Commons from '../../../Services/Common/Common';
import dayjs from 'dayjs';

/**
 * 위험성평가하기(RIN_DAI) 스키마
 */
export const RIN_DAI_Schema = {
  fields: {
    no: { initial: '', label: 'NO' },
    // 위험성평가하기 일련번호 (키값, NearMissSeq)
    nearMissSeq: { initial: null, label: '순번' },
    gubun: { initial: '', label: '구분', required: 'Y' },
    occurDate: { initial: '', label: '발생일시' },
    workStartDate: { initial: '', label: '작업시작시간' },
    workEndDate: { initial: '', label: '작업종료시간' },
    depNm: { initial: '', label: '부서', required: 'Y' },
    rank: { initial: '', label: '직급' },
    discovererNm: { initial: '', label: '성명', required: 'Y' },
    managerNm: { initial: '', label: '담당자' },
    managerId: { initial: '', label: '담당자ID' },
    location: { initial: '', label: '발생장소' },
    gongjongCd: { initial: '', label: '공정코드' },
    gongjongNm: { initial: '', label: '공정(기인물)' },
    todaySafety: { initial: '', label: '현재의 안전보건조치' },
    reductionMeasures: { initial: '', label: '위험성 감소대책' },
    isRisk: { initial: 'Y', label: '위험성평가 근로자 의견 청취 받기 여부' },
    content: { initial: '', label: '유해위험요인' },
    resultContent: { initial: '', label: '조치결과' },
    preFileNames: { initial: '', label: '개선전 사진' },
    postFileNames: { initial: '', label: '개선 후 사진' },
    resultDate: { initial: '', label: '조치일자' },
    status: { initial: '', label: '상태' },
    remark: { initial: '', label: '비고' },
  },
  mapper: {
    /**
     * GetAccBasic02R1 조회 — 서버 액션·Dapper 파라미터와 맞출 것
     * SQL의 @AuthCd, @UserId를 쓰면 컨트롤러/리포지토리에서 반드시 동일 이름으로 바인딩.
     * @AuthCd 미전달 시 MySQL: "Fatal error encountered during command execution" 다발.
     */
    search1: {
      CustCd: (_, context) =>
        Commons.EncryptAES(context.search?.custCd || '') || '',
      DepCd: (_, context) =>
        Commons.EncryptAES(context.search?.depCd || '') || '',
      Year: (_, context) =>
        Commons.EncryptAES(context.search?.year || '') || '',
      Month: (_, context) =>
        Commons.EncryptAES(context.search?.month || '') || '',
      GongjongCd: (_, context) =>
        Commons.EncryptAES(context.search?.gongjongCd || '') || '',
      Status: (_, context) =>
        Commons.EncryptAES(context.search?.gubun || '') || '',
      Name: (_, context) =>
        Commons.EncryptAES(context.search?.searchText || '') || '',
      AuthCd: (_, context) =>
        Commons.EncryptAES(context.userInfo?.authCd ?? '') || '',
      UserId: (_, context) =>
        Commons.EncryptAES(
          context.userInfo?.userid ?? context.userInfo?.userId ?? ''
        ) || '',
    },
    /**
     * SaveAccBasic02U1 요청: SaveAccBasic02U1Info (서버 DTO)
     * NearMissSeq(0=신규), CustCd, Gubun(1/2), GongjongCd, OccurDate, Location,
     * Content, ResultContent, ResultDate, DiscovererId, ManagerId, Status(0/1), Remark, UserId, Images
     */
    save1: {
      NearMissSeq: (row) => {
        const v = row.nearMissSeq;
        if (v == null || v === '' || v === undefined) return 0;
        const n = Number(v);
        return Number.isInteger(n) && n > 0 ? n : 0;
      },
      CustCd: (_, context) =>
        Commons.EncryptAES(context.search?.custCd || '') || '',
      // Gubun은 반드시 코드값('1','2','3')으로 저장되도록 라벨 → 코드 변환
      Gubun: (row) => {
        const raw = row.gubun ?? '';
        let code = raw;
        const rawStr = typeof raw === 'string' ? raw.replace(/\s+/g, '') : '';
        if (raw === '위험요인 제보' || rawStr === '위험요인제보') code = '1';
        else if (raw === '아차사고 상황' || rawStr === '아차사고상황')
          code = '2';
        else if (raw === '작업종료' || rawStr === '작업종료') code = '3';
        return Commons.EncryptAES(code || '') || '';
      },
      GongjongCd: (row) => Commons.EncryptAES(row.gongjongCd ?? '') || '',
      OccurDate: (row) => {
        const v = row.occurDate;
        if (!v) return Commons.EncryptAES('') || '';
        const formatted = dayjs(v).isValid()
          ? dayjs(v).format('YYYY-MM-DD HH:mm:ss')
          : '';
        return Commons.EncryptAES(formatted) || '';
      },
      Location: (row) => Commons.EncryptAES(row.location ?? '') || '',
      // 현재의 안전보건조치 / 위험성 감소대책
      // - grid key: todaySafety / reductionMeasures
      // - 서버: TODAY_SAFETY / TodaySafety, REDUCTION_MEASURES / ReductionMeasures 중 어느 네이밍이든 바인딩되도록 둘 다 전송
      TodaySafety: (row) =>
        Commons.EncryptAES(
          row.todaySafety ?? row.TodaySafety ?? row.TODAY_SAFETY ?? ''
        ) || '',
      ReductionMeasures: (row) =>
        Commons.EncryptAES(
          row.reductionMeasures ??
            row.ReductionMeasures ??
            row.REDUCTION_MEASURES ??
            ''
        ) || '',
      // 위험감지·아차사고 화면: 구분과 무관 항상 Y
      IsRisk: () => Commons.EncryptAES('Y') || '',
      Content: (row) => Commons.EncryptAES(row.content ?? '') || '',
      ResultContent: (row) => Commons.EncryptAES(row.resultContent ?? '') || '',
      ResultDate: (row) => {
        const v = row.resultDate;
        if (!v) return '';
        const formatted = dayjs(v).isValid()
          ? dayjs(v).format('YYYY-MM-DD HH:mm:ss')
          : '';
        return formatted ? Commons.EncryptAES(formatted) || '' : '';
      },
      DiscovererId: (row) => Commons.EncryptAES(row.discovererId ?? '') || '',
      ManagerId: (row) => Commons.EncryptAES(row.managerId ?? '') || '',
      Status: (row) => Commons.EncryptAES(row.status ?? '') || '',
      Remark: (row) => Commons.EncryptAES(row.remark ?? '') || '',
      UserId: (_, context) => context.userInfo?.userid || '',
      // 이미지 목록: TB_BS_NEAR_MISS_IMAGE에 여러 행 INSERT. DELETE가 NEAR_MISS_SEQ 전체 삭제이므로 PRE·POST를 한 번에 모두 보내야 함 (한쪽만 보내면 다른 쪽이 삭제됨)
      // fileName = 원본 파일명, saveName = 년월일시분초_인덱스_파일명, filePath = AccidentStatus/${custCd}/ACS_BASIC_02/Pre|Post/saveName
      Images: (row, context) => {
        const list = [];
        const custCd = context?.search?.custCd || '';

        const pushImageRows = (
          fileNamesStr,
          folderPath,
          saveNamesStr,
          imgType,
          defaultFolder
        ) => {
          const names = String(fileNamesStr || '')
            .split('|')
            .filter(Boolean);
          if (names.length === 0) return;
          const folder =
            folderPath ||
            `AccidentStatus/${custCd}/ACS_BASIC_02/${defaultFolder}`;
          const saves = saveNamesStr
            ? String(saveNamesStr).split('|').filter(Boolean)
            : names;
          const maxLen = Math.max(names.length, saves.length);
          for (let i = 0; i < maxLen; i += 1) {
            const fileName = names[i] ?? names[names.length - 1] ?? '';
            const saveName =
              saves[i] ?? saves[saves.length - 1] ?? names[i] ?? '';
            if (!fileName || !saveName) continue;
            // DB 저장 경로: AccidentStatus/${custCd}/ACS_BASIC_02/Pre|Post/saveName
            const fullPath = `${folder}/${saveName}`;
            list.push({
              ImgType: imgType,
              ImageFileName: fileName,
              ImageFilePath: fullPath,
              ImageSaveName: saveName,
            });
          }
        };

        // 개선 전(PRE) – preFileNames만 있어도 경로/저장명 보정해서 포함
        if (row.preFileNames) {
          pushImageRows(
            row.preFileNames,
            row.preFilePath,
            row.preSaveName,
            'PRE',
            'Pre'
          );
        }
        // 개선 후(POST) – postFileNames만 있어도 경로/저장명 보정해서 포함
        if (row.postFileNames) {
          pushImageRows(
            row.postFileNames,
            row.postFilePath,
            row.postSaveName,
            'POST',
            'Post'
          );
        }

        return list;
      },
    },
    /** DeleteAccBasic02D1 요청: CustCd(암호화), NearMissSeq */
    delete1: {
      CustCd: (_, context) =>
        Commons.EncryptAES(context.search?.custCd || '') || '',
      NearMissSeq: (row) => {
        const v = row.nearMissSeq ?? row.NearMissSeq;
        if (v == null || v === '') return 0;
        const n = Number(v);
        return Number.isInteger(n) ? n : parseInt(v, 10) || 0;
      },
    },
  },
};

/**
 * 서버 GetAccBasic02R1: 메인 쿼리 + GetAccBasic02R1_Images로 행별 Images 배열 매핑.
 * 행에 바인딩 시 Images 배열을 기준으로 PRE/POST 파일명·경로·저장명을 채움.
 */
export const mapGetAcsBasic02R1RowToGrid = (row) => {
  if (!row) return null;
  /** 서버 DateTime/문자열 → 그리드용 문자열 */
  const toGridDateTime = (v) => {
    if (v == null || v === '') return '';
    if (typeof v === 'string') return v;
    return dayjs(v).isValid() ? dayjs(v).format('YYYY-MM-DD HH:mm:ss') : '';
  };
  const toGridDateOnly = (v) => {
    if (v == null || v === '') return '';
    if (typeof v === 'string') return v.split('T')[0] || v;
    return dayjs(v).isValid() ? dayjs(v).format('YYYY-MM-DD') : '';
  };

  const images = row.Images ?? row.images ?? [];

  const preImages = images.filter(
    (img) => (img.ImgType ?? img.imgType) === 'PRE'
  );
  const postImages = images.filter(
    (img) => (img.ImgType ?? img.imgType) === 'POST'
  );

  const toPipe = (arr, key) =>
    arr
      .map(
        (img) =>
          img[key] ??
          img[key.replace(/(.)(.*)/, (_, a, b) => a.toLowerCase() + b)] ??
          ''
      )
      .join('|');
  // ImageFilePath가 '폴더/파일명.확장자' 전체 경로일 수 있음 → 폴더만 추출 (마지막 '/' 앞까지)
  const toFolder = (path) =>
    path && String(path).includes('/')
      ? String(path).replace(/\/[^/]+$/, '')
      : path || '';
  const preFileNames = preImages.length
    ? toPipe(preImages, 'ImageFileName')
    : (row.preFileNames ?? row.PreFileNames ?? '');
  const preSaveName = preImages.length
    ? toPipe(preImages, 'ImageSaveName')
    : (row.preSaveName ?? row.PreSaveNames ?? '');
  const preFilePath = preImages.length
    ? toFolder(preImages[0]?.ImageFilePath ?? preImages[0]?.imageFilePath ?? '')
    : (row.preFilePath ?? row.PreFilePaths ?? '');
  const postFileNames = postImages.length
    ? toPipe(postImages, 'ImageFileName')
    : (row.postFileNames ?? row.PostFileNames ?? '');
  const postSaveName = postImages.length
    ? toPipe(postImages, 'ImageSaveName')
    : (row.postSaveName ?? row.PostSaveNames ?? '');
  const postFilePath = postImages.length
    ? toFolder(
        postImages[0]?.ImageFilePath ?? postImages[0]?.imageFilePath ?? ''
      )
    : (row.postFilePath ?? row.PostFilePaths ?? '');

  let gubunCode = row.gubun ?? row.Gubun ?? '';
  const gubunNorm =
    typeof gubunCode === 'string' ? gubunCode.replace(/\s+/g, '') : '';
  if (gubunCode === '위험요인 제보' || gubunNorm === '위험요인제보')
    gubunCode = '1';
  else if (gubunCode === '아차사고 상황' || gubunNorm === '아차사고상황')
    gubunCode = '2';
  else if (gubunCode === '작업종료' || gubunNorm === '작업종료')
    gubunCode = '3';
  let statusCode = row.status ?? row.Status ?? '';
  if (statusCode === '확인 전') statusCode = '0';
  else if (statusCode === '완료') statusCode = '1';

  const seq = row.nearMissSeq ?? row.NearMissSeq ?? null;

  let gongjongCd = row.gongjongCd ?? row.GongjongCd ?? '';
  let gongjongNm = row.gongjongNm ?? row.GongjongNm ?? '';
  const cdTrim = String(gongjongCd ?? '').trim();
  const nmTrim = String(gongjongNm ?? '').trim();
  if (
    cdTrim === '0' ||
    cdTrim === '00' ||
    nmTrim === '기타' ||
    cdTrim === '기타'
  ) {
    gongjongCd = '0';
    gongjongNm = '기타';
  }

  return {
    // 서버 조회에는 NO 없음 → NO 컬럼 표시용으로 NearMissSeq 사용
    no: row.no ?? row.No ?? (seq != null && seq !== '' ? String(seq) : ''),
    nearMissSeq: seq,
    occurDate: toGridDateTime(row.occurDate ?? row.OccurDate),
    createDate: toGridDateTime(row.createDate ?? row.CreateDate),
    workStartDate: toGridDateTime(
      row.workStartDate ?? row.WorkStartDate ?? row.WORK_START_DATE
    ),
    workEndDate: toGridDateTime(
      row.workEndDate ?? row.WorkEndDate ?? row.WORK_END_DATE
    ),
    custNm: row.custNm ?? row.CustNm ?? '',
    gubun: gubunCode,
    depNm: row.depNm ?? row.DepNm ?? '',
    rank: row.rank ?? row.Rank ?? '',
    discovererId: row.discovererId ?? row.DiscovererId ?? '',
    discovererNm: row.discovererNm ?? row.DiscovererNm ?? '',
    location: row.location ?? row.Location ?? '',
    gongjongCd,
    gongjongNm,
    todaySafety: row.todaySafety ?? row.TodaySafety ?? row.TODAY_SAFETY ?? '',
    reductionMeasures:
      row.reductionMeasures ??
      row.ReductionMeasures ??
      row.REDUCTION_MEASURES ??
      '',
    isRisk: row.isRisk ?? row.IsRisk ?? 'Y',
    content: row.content ?? row.Content ?? '',
    resultContent: row.resultContent ?? row.ResultContent ?? '',
    managerId: row.managerId ?? row.ManagerId ?? '',
    managerNm: row.managerNm ?? row.ManagerNm ?? '',
    status: statusCode,
    resultDate: toGridDateOnly(row.resultDate ?? row.ResultDate),
    preFileNames,
    preFilePath,
    preSaveName,
    postFileNames,
    postFilePath,
    postSaveName,
    images,
    remark: row.remark ?? row.Remark ?? '',
    newYn: row.newYn ?? 'N',
    editYn: row.editYn ?? 'N',
    deleteYn: row.deleteYn ?? 'N',
  };
};
